import React from 'react';
import { Link } from 'react-router-dom';
import { servicesPage } from '../data/mockData';

export const ServicesPage: React.FC = () => {
  return (
    <div className="bg-background-light min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block py-1 px-3 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-5">
              {servicesPage.hero.badge}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 leading-[1.15] tracking-tight mb-5">
              {servicesPage.hero.title}
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed max-w-xl">
              {servicesPage.hero.description}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="#services-grid"
                className="bg-primary text-white px-7 py-3.5 rounded-md font-medium text-base hover:bg-primary-dark transition-colors"
              >
                {servicesPage.hero.ctaPrimary}
              </Link>
              <Link
                to="/contact"
                className="border border-slate-300 text-slate-700 hover:border-primary hover:text-primary px-7 py-3.5 rounded-md font-medium text-base transition-colors"
              >
                {servicesPage.hero.ctaSecondary}
              </Link>
            </div>
          </div>
          <div className="relative">
            <img
              src={servicesPage.hero.image.src}
              alt={servicesPage.hero.image.alt}
              className="rounded-xl shadow-lg w-full h-[420px] lg:h-[500px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section id="services-grid" className="py-20 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4 tracking-tight">{servicesPage.servicesGrid.title}</h2>
            <p className="text-slate-600 max-w-2xl mx-auto leading-relaxed">{servicesPage.servicesGrid.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {servicesPage.servicesGrid.services.map((service) => (
              <div
                key={service.title}
                className="bg-white p-7 rounded-xl border border-slate-200 flex flex-col hover:border-primary/40 hover:shadow-md transition-all"
              >
                <div className={`w-12 h-12 ${service.color === 'accent' ? 'bg-primary-deep' : 'bg-primary/10'} rounded-lg flex items-center justify-center ${service.color === 'accent' ? 'text-accent-gold' : 'text-primary'} mb-5`}>
                  <span className="material-symbols-outlined text-2xl">{service.icon}</span>
                </div>
                <h3 className="text-lg font-semibold mb-3">{service.title}</h3>
                <p className="text-slate-600 mb-5 flex-grow leading-relaxed text-sm">{service.description}</p>
                <ul className="space-y-2.5 mb-6 text-sm text-slate-700">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2.5">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary flex-shrink-0">
                        <span className="material-symbols-outlined text-xs">check</span>
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button className="w-full py-2.5 px-4 rounded-md bg-slate-50 text-primary font-medium hover:bg-primary hover:text-white transition-colors text-sm">
                  Learn More
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NDIS Process Section */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">
            <div className="lg:w-1/2">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-5 tracking-tight">{servicesPage.process.title}</h2>
              <p className="text-base lg:text-lg text-slate-600 mb-9 leading-relaxed">
                {servicesPage.process.description}
              </p>
              <div className="space-y-7">
                {servicesPage.process.steps.map((step) => (
                  <div key={step.number} className="flex gap-4">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center font-semibold text-sm">
                      {step.number}
                    </div>
                    <div className="pt-0.5">
                      <h4 className="text-lg font-semibold mb-1.5">{step.title}</h4>
                      <p className="text-slate-600 leading-relaxed text-sm">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:w-1/2">
              <div className="bg-slate-50 p-8 lg:p-10 rounded-xl border border-slate-200">
                <h4 className="text-xl font-bold mb-5 flex items-center gap-3">
                  <span className="material-symbols-outlined text-primary text-2xl">help_center</span>
                  {servicesPage.categories.title}
                </h4>
                <p className="text-slate-700 mb-6 leading-relaxed text-sm">
                  {servicesPage.categories.subtitle}
                </p>
                <div className="space-y-3">
                  {servicesPage.categories.items.map((category) => (
                    <div key={category.title} className="p-4 bg-white rounded-lg border border-slate-200">
                      <h5 className="font-semibold text-primary mb-1">{category.title}</h5>
                      <p className="text-sm text-slate-500 leading-relaxed">{category.description}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <p className="text-sm text-slate-500 italic">{servicesPage.categories.footerNote}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-5 tracking-tight">{servicesPage.cta.title}</h2>
          <p className="text-lg text-white/85 mb-9 max-w-2xl mx-auto leading-relaxed">
            {servicesPage.cta.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/contact"
              className="bg-white text-primary px-8 py-3.5 rounded-md font-semibold text-base hover:bg-slate-100 transition-colors"
            >
              {servicesPage.cta.primary}
            </Link>
            <Link
              to="/contact"
              className="border border-white/40 text-white px-8 py-3.5 rounded-md font-semibold text-base hover:bg-white/10 transition-colors"
            >
              {servicesPage.cta.secondary}
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-white/80">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-xl">call</span>
              <span className="font-medium">{servicesPage.cta.phone}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-xl">mail</span>
              <span className="font-medium">{servicesPage.cta.email}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
